import { CART_LOCAL_STORAGE_KEY } from 'constants/appConstants';
import { loadFromLocalStorage, saveToLocalStorage } from 'helpers/localStorageUtilities';
import { IS_EGC_NAME } from 'common/regex';

export const groupCartItemsBySizePrediction = (cartItems = []) => {
  const groupedCartItems = cartItems.reduce((acc, item) => {
    if (item?.sizing?.guidance) {
      if (item.sizing.guidance.isRecommendedSize) {
        acc.itemsWithFitRecommended.push(item);
      } else {
        acc.itemsWithFitNotRecommended.push(item);
      }
    } else {
      acc.itemsWithNoRecommendationAvailable.push(item);
    }
    return acc;
  }, { // Downstream-safe defaults for activeItems in cart
    itemsWithFitRecommended: [],
    itemsWithFitNotRecommended: [],
    itemsWithNoRecommendationAvailable: []
  });
  return groupedCartItems;
};

export const makeActiveItemsArray = cart => {
  const items = cart?.activeItems || [];
  const newItems = [];

  items.forEach(item => {
    const {
      asin,
      stockId,
      quantity,
      price,
      egc,
      productName,
      recipientEmail,
      recipientName,
      senderName,
      message
    } = item;

    let newItem = {
      asin,
      stockId,
      quantity
    };

    if (egc || IS_EGC_NAME.test(productName)) { // checkout does not provide `egc` boolean, so use productname regex instead
      newItem = {
        ...newItem,
        itemType: 'egc',
        egc: true,
        recipientEmail,
        recipientName,
        amount: price, // the param it takes is `amount` but the value in Mafia is called `price`
        senderName,
        message
      };
    }

    newItems.push(newItem);
  });

  return newItems;
};

export const getIdentifierMismatch = ({ cookiesXMain, cookiesSessionId }) => {
  const cartLocalStorage = loadFromLocalStorage(CART_LOCAL_STORAGE_KEY);

  if (!cartLocalStorage) {
    return {};
  }

  const {
    xMain: lsXMain,
    sessionId: lsSessionId
  } = cartLocalStorage || {};

  const identifiers = {};
  identifiers.hasXMain = !!cookiesXMain;
  identifiers.isXMainMismatched = (cookiesXMain && cookiesXMain !== lsXMain) ? true : false;
  identifiers.isSessionIdMismatched = cookiesSessionId !== lsSessionId ? true : false;

  return identifiers;
};

export const mergeBrowserStorageCart = changeQuantityPayload => {
  const { items: dataItems } = changeQuantityPayload;
  const { items: localStorageItems = [] } = loadFromLocalStorage(CART_LOCAL_STORAGE_KEY) || {};

  // if local storage cart doesn't exist, just return active items
  if (!localStorageItems?.length) {
    return [...dataItems];
  }

  // if there are items in local storage, and there's exactly one item in your cart
  if (localStorageItems.length && dataItems.length === 1) {
    const { asin, stockId, quantity } = dataItems[0];
    const matchingItem = localStorageItems.find(item => (asin && item.asin === asin) || (stockId && item.stockId === stockId));

    // if there is a matching item from cart and local storage, sum the quantities and return early
    if (matchingItem) {
      matchingItem.quantity += quantity;
      return [...localStorageItems];
    }
  }
  // otherwise just concat the two and return
  return [...localStorageItems, ...dataItems];
};

export const storeCartToLocalStorage = (cart, xMain, sessionId) => {
  saveToLocalStorage(CART_LOCAL_STORAGE_KEY, {
    xMain,
    sessionId,
    items: makeActiveItemsArray(cart)
  });
};

export const clearCartLocalStorage = () => {
  localStorage.removeItem(CART_LOCAL_STORAGE_KEY);
};
